'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { useQuery } from '@apollo/client';
import { GET_ITEM_REQUEST_SERVICE_LIST } from '../../core/graphql/queries/getItemRequestServices';
import type {
  GetItemRequestServiceListData,
  GetItemRequestServiceListVariables
} from '../../core/graphql/queries/getItemRequestServices';
import { ENV } from '@/core/config/env';
import { env } from 'process';
import { usePopup } from '@/contexts/PopupContext';

// Custom SVG icons to replace Heroicons
const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

interface ProductServiceCarouselProps {
  className?: string;
  [key: string]: any;
}

interface ItemService {
  itemRequestServiceID?: string;
  itemRequestServiceTitle: string;
  itemRequestServiceDescription: string;
  uploadPath: string;
  thumbNailPath: string;
  domainCategoryID: string;
  subCategoryID: string;
  categoryID: string;
}

const ProductServiceCarousel: React.FC<ProductServiceCarouselProps> = ({ className = '', ...props }) => {
  // Use the popup context
  const { isPopupOpen, setPopupOpen, popupType, setPopupType } = usePopup();
  
  // Keep only selectedService and carousel-specific state
  const [selectedService, setSelectedService] = useState<ItemService | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(6);

  // GraphQL query
  const { data, loading, error } = useQuery<
    GetItemRequestServiceListData,
    GetItemRequestServiceListVariables
  >(
    GET_ITEM_REQUEST_SERVICE_LIST,
    {
      variables: {
        domainCategoryIds: env.REQUEST_SERVICE_CATEGORY_ID ?? '',
        categoryId: null,
        subCategoryId: null,
        page: 1,
        size: 10
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-first'
    }
  );
    
  const services = data?.getItemRequestServiceList?.result || [];

  // Responsive items per view
  useEffect(() => {
    const updateItemsPerView = () => {
      if (typeof window === 'undefined') return;
      
      if (window.innerWidth < 640) setItemsPerView(1);
      else if (window.innerWidth < 768) setItemsPerView(2);
      else if (window.innerWidth < 1024) setItemsPerView(3);
      else if (window.innerWidth < 1280) setItemsPerView(4);
      else if (window.innerWidth < 1536) setItemsPerView(6);
      //else if (window.innerWidth < 1920) setItemsPerView(8);
      else setItemsPerView(6);
    };

    updateItemsPerView();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateItemsPerView);
      return () => window.removeEventListener('resize', updateItemsPerView);
    }
  }, []);

  // Navigation functions
  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => 
      prev + itemsPerView >= services.length ? 0 : prev + itemsPerView
    );
  }, [itemsPerView, services.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => 
      prev - itemsPerView < 0 ? Math.max(0, services.length - itemsPerView) : prev - itemsPerView
    );
  }, [itemsPerView, services.length]);

  // Auto-advance carousel
  useEffect(() => {
    if (services.length <= itemsPerView) return;
    
    const interval = setInterval(nextSlide, 50000);
    return () => clearInterval(interval);
  }, [nextSlide, services.length, itemsPerView]);

  // Event handlers
  const handleItemClick = useCallback((item: ItemService) => {
    try {
      console.log('🔍 Carousel item clicked:', item);
      
      // Only open if no other popup is open
      if (!isPopupOpen) {
        // Store the selected service data safely
        if (typeof window !== 'undefined' && window.sessionStorage) {
          const serviceData = {
            title: item.itemRequestServiceTitle || '',
            description: item.itemRequestServiceDescription || '',
            serviceId: item.itemRequestServiceID || '',
            categoryId: item.categoryID || '',
            subCategoryId: item.subCategoryID || '',
            domainCategoryId: item.domainCategoryID || ''
          };
          
          sessionStorage.setItem('selectedServiceData', JSON.stringify(serviceData));
          console.log('✅ Stored service data:', serviceData);
        }
        
        setSelectedService(item);
        setPopupType('carousel');
        setPopupOpen(true);
        
        console.log('✅ Popup state updated');
      } else {
        console.log('❌ Popup already open, ignoring click');
      }
    } catch (error) {
      console.error('❌ Error handling item click:', error);
    }
  }, [isPopupOpen, setPopupOpen, setPopupType]);

  // Show loading state
  if (loading) {
    return (
      <section className={`py-12 bg-gray-50 w-full ${className}`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 max-w-7xl">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-300 h-32 rounded-lg mb-3"></div>
                  <div className="bg-gray-300 h-4 rounded mb-2"></div>
                  <div className="bg-gray-300 h-8 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className={`py-12 bg-gray-50 w-full ${className}`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-2xl">
              <h3 className="text-red-800 font-medium">Unable to load services</h3>
              <p className="text-red-600 text-sm mt-1">{error.message}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Don't render if no services
  if (!services.length) return null;

  const visibleServices = services.slice(currentIndex, currentIndex + itemsPerView);
  const showNavigation = services.length > itemsPerView;

  return (
    <>
      <section className={`py-12 bg-gray-50 w-full ${className}`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Popular Automotive Queue For You Services & Car Parts Request & Compare Quotes
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Request or Browse Car Parts Online Quick and Easily
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative flex justify-center">
            <div className="relative max-w-7xl w-full">
              {/* Navigation Buttons */}
              {showNavigation && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors duration-200"
                    aria-label="Previous services"
                  >
                    <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors duration-200"
                    aria-label="Next services"
                  >
                    <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                  </button>
                </>
              )}

              {/* Services Grid - Centered with Better Spacing */}
              <div className="flex justify-center px-12">
                <div 
                  className="grid gap-6 sm:gap-8" 
                  style={{
                    gridTemplateColumns: `repeat(${Math.min(visibleServices.length, itemsPerView)}, minmax(0, 1fr))`,
                    maxWidth: 'fit-content'
                  }}
                >
                  {visibleServices.map((service, index) => (
                    <ServiceCard
                      key={`${service.itemRequestServiceTitle}-${currentIndex}-${index}`}
                      item={service}
                      onClick={() => handleItemClick(service)}
                    />
                  ))}
                </div>
              </div>

              {/* Pagination Dots */}
              {showNavigation && (
                <div className="flex justify-center mt-8 space-x-2">
                  {Array.from({ length: Math.ceil(services.length / itemsPerView) }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index * itemsPerView)}
                      className="w-6 h-6 flex items-center justify-center"
                      aria-label={`Go to page ${index + 1}`}
                    >
                      <span className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                        Math.floor(currentIndex / itemsPerView) === index
                          ? 'bg-blue-600'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Service Card Component - Updated with consistent sizing and better spacing
const ServiceCard: React.FC<{
  item: ItemService;
  onClick: () => void;
}> = React.memo(({ item, onClick }) => {
  const imageUrl = `${process.env.NEXT_PUBLIC_IMAGE_URL || ''}${item.uploadPath}`;
  const fallbackImageUrl = `${process.env.NEXT_PUBLIC_IMAGE_URL || '/images'}${item.thumbNailPath}`;

  const handleClick = useCallback((e: React.MouseEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      console.log('🔍 ServiceCard clicked:', item.itemRequestServiceTitle);
      onClick();
    } catch (error) {
      console.error('❌ Error in ServiceCard click:', error);
    }
  }, [onClick, item.itemRequestServiceTitle]);

  const handleButtonClick = useCallback((e: React.MouseEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      console.log('🔍 Request button clicked:', item.itemRequestServiceTitle);
      onClick();
    } catch (error) {
      console.error('❌ Error in button click:', error);
    }
  }, [onClick, item.itemRequestServiceTitle]);

  return (
    <div 
      className="group cursor-pointer bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden w-48 flex-shrink-0 transform hover:-translate-y-1"
      onClick={handleClick}
    >
      <div className="relative aspect-video bg-gray-200">
        <Image
          src={imageUrl}
          alt={item.itemRequestServiceTitle}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-300"
          sizes="192px"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src === imageUrl) {
              target.src = fallbackImageUrl;
            } else {
              target.src = '/images/placeholder.jpg';
            }
          }}
        />
      </div>
      
      <div className="p-5 flex flex-col">
        <div className="h-12 flex items-start mb-3">
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight">
            {item.itemRequestServiceTitle}
          </h3>
        </div>
        
        <button 
          type="button"
          onClick={handleButtonClick}
          className="w-full bg-blue-600 text-white text-xs py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2 font-medium"
          aria-label={`Request quote for ${item.itemRequestServiceTitle}`}
        >
          <span>Request for Quote</span>
          <ArrowRightIcon className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
});

ServiceCard.displayName = 'ServiceCard';

export default ProductServiceCarousel;